// SMS segment counting and SMS-vs-MMS classification, shared by the send
// worker, the composer preview and the app. Import-free (no Deno.*, no
// @supabase/supabase-js) so edge functions, src/test/*.test.ts and the Vite
// app (via src/utils/smsSegments.ts) all import it by relative path.

// Character sets defined by the GSM 03.38 SMS standard.
const GSM_7_REGEX = /^[A-Za-z0-9 \r\n\f@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}[\]\\~|€]*$/;
// The GSM 03.38 extension table: each of these costs two septets.
const GSM_7_EXTENDED_CHARS = /^[\f^{}[\]\\~|€]$/;

/** True when a single character fits the GSM-7 basic or extension table.
 *  One non-GSM-7 character anywhere flips the whole message to UCS-2, which
 *  is why the composer's cost meter needs the per-character test. */
export const isGsm7Char = (ch: string): boolean => GSM_7_REGEX.test(ch);

export const calculateSmsSegments = (text: string) => {
  const isGsm7 = GSM_7_REGEX.test(text);
  const encoding: "GSM-7" | "UCS-2" = isGsm7 ? "GSM-7" : "UCS-2";

  let charsPerSegment: number;
  let charsPerMultiSegment: number;

  if (encoding === "GSM-7") {
    charsPerSegment = 160;
    charsPerMultiSegment = 153;
  } else {
    charsPerSegment = 70;
    charsPerMultiSegment = 67;
  }

  let charCount = 0;
  if (isGsm7) {
    for (let i = 0; i < text.length; i++) {
      charCount += GSM_7_EXTENDED_CHARS.test(text[i]) ? 2 : 1;
    }
  } else {
    charCount = text.length;
  }

  if (charCount <= charsPerSegment) {
    return {
      segments: 1,
      encoding,
      charsPerSegment,
      charsLeft: charsPerSegment - charCount,
    };
  }

  const segments = Math.ceil(charCount / charsPerMultiSegment);
  const totalCharsInSegments = segments * charsPerMultiSegment;
  const charsLeft = totalCharsInSegments - charCount;

  return {
    segments,
    encoding,
    charsPerSegment: charsPerMultiSegment,
    charsLeft,
  };
};

/**
 * A message is an MMS when it carries an image, or when its text spills past a
 * single GSM-7 segment (160 chars). RingCentral bills and routes MMS
 * differently, so the worker records which one it sent.
 */
export function isMms(body: string, hasImage: boolean): boolean {
  if (hasImage) return true;
  return calculateSmsSegments(body).segments > 1;
}
